/**
 * Free India Postal Pincode Lookup Service (Amazon/Flipkart Style)
 * Zero API keys required. Uses official Postal Pincode Directory API with local caching & instant fallbacks.
 */

// Local fallback dictionary for instant response on key regional pincodes
const LOCAL_PINCODE_DATA = {
  // Madurai PIN Codes
  "625001": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Madurai Main", "Simmakkal", "Town Hall Road", "Meenakshi Temple"] },
  "625002": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Tallakulam", "Chinna Chokkikulam", "Gokhale Road", "Goripalayam"] },
  "625003": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Alagappa Nagar", "Kalavasal", "Sundarajapuram"] },
  "625004": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Pasumalai", "Thirunagar", "Pykara"] },
  "625005": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Thiruparankundram", "Harveypatti", "Nilaiyur"] },
  "625006": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Tirunagar", "Thanakankulam"] },
  "625007": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["K. Pudur", "Surya Nagar", "Moondrumavadi"] },
  "625008": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Kappalur", "SIDCO Industrial Estate"] },
  "625009": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Anuppanadi", "Teppakulam", "Sourashtra Colony"] },
  "625010": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Kochadai", "Ponmeni", "PP Chavadi"] },
  "625011": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Subramaniapuram", "Jaihindpuram", "Solai Alagupuram"] },
  "625012": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Avaniyapuram", "Villapuram", "Meenakshi Nagar"] },
  "625014": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Krishnapuram Colony", "Chatrapatti", "Iyer Bungalow"] },
  "625016": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["SS Colony", "Ellis Nagar", "Arasaradi", "Mahaboobpalayam"] },
  "625017": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Anaiyur", "Koodal Nagar", "Sikandar Chavadi"] },
  "625018": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Vilangudi", "Shanthi Nagar", "Fathima Nagar"] },
  "625019": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Nagamalai", "Pudukkottai", "Thuvariman"] },
  "625020": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Anna Nagar", "KK Nagar", "Gandhi Nagar", "Vandiyur", "Sadasiva Nagar"] },
  "625021": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Palkalai Nagar", "MKU Campus", "Vadapalanji"] },
  "625022": { city: "Madurai", district: "Madurai", state: "Tamil Nadu", localities: ["Perungudi", "Kudakoil", "Airport Area"] },

  // Peraiyur & Surrounding PIN Codes
  "625703": { city: "Peraiyur", district: "Madurai", state: "Tamil Nadu", localities: ["Peraiyur Main", "Koovalapuram", "Sandaiyur", "Silaimalaipatti", "Pappayapuram", "Deivanayagapuram", "Salichandai", "Nagayapuram", "S. Keelapatti", "Vairavi Ammapatti", "Poosalapuram"] },
  "625705": { city: "Peraiyur", district: "Madurai", state: "Tamil Nadu", localities: ["Saptur", "Kudipatti"] },
  "625527": { city: "Sedapatti", district: "Madurai", state: "Tamil Nadu", localities: ["Sedapatti", "Kudiseri"] },
  "625702": { city: "T. Kallupatti", district: "Madurai", state: "Tamil Nadu", localities: ["T. Kallupatti", "Kadaneri", "Karaikeni", "Velambur"] },
  "625535": { city: "Elumalai", district: "Madurai", state: "Tamil Nadu", localities: ["Elumalai Main", "Uthapuram"] },
  "625706": { city: "Tirumangalam", district: "Madurai", state: "Tamil Nadu", localities: ["Tirumangalam Main", "Kappalur"] },
  "625106": { city: "Melur", district: "Madurai", state: "Tamil Nadu", localities: ["Melur Town", "Therkku Theru"] },
  "625501": { city: "Usilampatti", district: "Madurai", state: "Tamil Nadu", localities: ["Usilampatti Town", "Valandur"] },
};

const memoryCache = new Map();

/**
 * Lookup PIN code information across India
 * @param {string} pincode 6-digit Indian Postal PIN code
 * @returns {Promise<{success: boolean, city?: string, district?: string, state?: string, country?: string, localities?: string[], message?: string}>}
 */
export async function lookupPincode(pincode) {
  const cleanPin = String(pincode || "").trim().replace(/\D/g, "");

  if (cleanPin.length !== 6) {
    return {
      success: false,
      message: "Please enter a valid 6-digit PIN code",
    };
  }

  // 1. Check in-memory session cache
  if (memoryCache.has(cleanPin)) {
    return memoryCache.get(cleanPin);
  }

  // 2. Check local quick lookup dictionary
  if (LOCAL_PINCODE_DATA[cleanPin]) {
    const data = {
      success: true,
      city: LOCAL_PINCODE_DATA[cleanPin].city,
      district: LOCAL_PINCODE_DATA[cleanPin].district,
      state: LOCAL_PINCODE_DATA[cleanPin].state,
      country: "India",
      localities: LOCAL_PINCODE_DATA[cleanPin].localities || [],
    };
    memoryCache.set(cleanPin, data);
    return data;
  }

  // 3. Fetch from Free India Post API
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);

    const response = await fetch(`https://api.postalpincode.in/pincode/${cleanPin}`, {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Postal API status ${response.status}`);
    }

    const data = await response.json();

    if (Array.isArray(data) && data[0]?.Status === "Success" && Array.isArray(data[0]?.PostOffice) && data[0].PostOffice.length > 0) {
      const offices = data[0].PostOffice;
      const first = offices[0];

      const localities = offices
        .map((po) => po.Name)
        .filter(Boolean)
        .map((name) => name.replace(/ B\.O$| S\.O$| H\.O$/i, "").trim());

      const uniqueLocalities = [...new Set(localities)];

      const result = {
        success: true,
        city: first.District || first.Block || first.Circle || first.Division || first.Region,
        district: first.District || first.Block || "",
        state: first.State || "Tamil Nadu",
        country: first.Country || "India",
        localities: uniqueLocalities,
      };

      memoryCache.set(cleanPin, result);
      return result;
    } else {
      return {
        success: false,
        message: "PIN code not found in postal registry. You can still enter your city manually.",
      };
    }
  } catch (error) {
    console.warn("Pincode lookup network fetch fallback:", error.message);
    // Graceful fallback without blocking the customer
    return {
      success: false,
      message: "Could not auto-fetch PIN code details. Please enter your city and state manually.",
    };
  }
}

/**
 * List of Indian States for dropdown fallback
 */
export const INDIAN_STATES = [
  "Tamil Nadu",
  "Kerala",
  "Karnataka",
  "Andhra Pradesh",
  "Telangana",
  "Maharashtra",
  "Puducherry",
  "Delhi",
  "Gujarat",
  "Rajasthan",
  "Uttar Pradesh",
  "West Bengal",
  "Madhya Pradesh",
  "Bihar",
  "Punjab",
  "Haryana",
  "Goa",
  "Assam",
  "Odisha",
  "Other",
];
